/* Analýza V2+ — propsání nefinančního majetku do klientské složky.
 *
 * ZAPOJENO 7. 8. 2026 (dřív tu stálo „INTEGRATION PENDING").
 * Volá to syncMajetek() v AnalyzaV2Page.js, ukládá se do /api/assets.
 * Most do složky (payloady + idempotentní sync) je na konci souboru.
 *
 * PROČ SAMOSTATNĚ:
 * syncSmlouvy() dnes propisuje smlouvy (stavebko, penzijko, OPF, KŽP,
 * úvěry, pojistky). Nefinanční majetek — byt, pronájem, auto, firma,
 * dědictví — nepropisuje vůbec, takže klientská složka o něm neví.
 *
 * PRAVIDLO MODULU:
 * Položku v cizí měně bez použitelného kurzu NEOCENÍME. hodnotaCzk je null,
 * uplne:false a důvod je vidět. Do složky se nikdy nesmí dostat číslo,
 * které nikdo neověřil — jinak by se objevilo v majetku klienta jako fakt.
 */

import { prepocet, normMena, DOMACI_MENA, datumKurzu, formatKurzu, duvodText } from "./meny";
import {
  maPrijem, nefinLabel, znamyTyp, druhMajetku,
  NEMOVITOST_BYDLENI, NEMOVITOST_PRONAJEM, KOMERCNI, POZEMEK, VOZIDLO, CENNOSTI, FIRMA, BUDOUCI,
} from "./nefinancni";

/* Řádky nefinančního majetku z původního datového modelu (n1–n5, f1, d1)
 * pole `typ` nemají. Odvodíme ho z jejich původního významu — jinak by se
 * poradci schovala pole, která už má vyplněná, a do složky by šel majetek
 * bez druhu. */
/* `n6` „Zlato a drahé kovy“ (nález kontroly 4. 10. 2026): řádek přibyl do
 * modelu (model.js — zlato se přestěhovalo z OPF do nefinančního majetku),
 * ale sem ne. Bez typu spadl na výchozí typ kategorie, tedy NEMOVITOST
 * K BYDLENÍ: ve složce klienta „Nemovitost k bydlení“ / druh „Nemovitost“,
 * ve formuláři pole Adresa a Datum pořízení, v podkladech nástrojů
 * (bydleniZAnalyzy) se cena zlata přičetla k hodnotě bydlení. Zlato je
 * CENNOSTI — typ i druh pro něj existují („Cennosti (hodinky, umění,
 * zlato)“, druh „Cennosti a zlato“, backend models_assets.py totéž).
 * Už propsaná položka se opraví sama: `type`/`kind` jsou pole analýzy,
 * příští sync je přepíše (zmenyMajetku → PATCH). */
export const VYCHOZI_TYP_NEFIN = {
  n1: NEMOVITOST_BYDLENI,
  n2: KOMERCNI,
  n3: POZEMEK,
  n4: VOZIDLO,
  n5: CENNOSTI,
  n6: CENNOSTI,
  f1: FIRMA,
  d1: BUDOUCI,
};

const num = (v) => (Number.isFinite(+v) ? +v : 0);
const txt = (v) => String(v ?? "").trim();
const arr = (v) => (Array.isArray(v) ? v : []);

/**
 * Typ položky nefinančního majetku.
 *
 * Pořadí: výslovný `typ` → typ podle id z datového modelu → výchozí podle
 * kategorie. Legacy řádek s vyplněným příjmem je fakticky pronájem — kdyby
 * dostal typ bez příjmu, zmizelo by poradci pole s jeho číslem.
 */
export function typNefin(row, vychozi = NEMOVITOST_BYDLENI) {
  const t = txt(row?.typ);
  if (znamyTyp(t)) return t;
  const podleId = VYCHOZI_TYP_NEFIN[txt(row?.id)];
  const zaklad = podleId || vychozi;
  if (num(row?.prijem) > 0 && !maPrijem(zaklad)) return NEMOVITOST_PRONAJEM;
  return zaklad;
}

/* Kategorie majetku, které se do složky propisují, a jejich výchozí typ. */
const ZDROJE = [
  { kat: "nemovitosti", vychozi: NEMOVITOST_BYDLENI },
  { kat: "firma", vychozi: FIRMA },
  { kat: "dedictvi", vychozi: BUDOUCI },
];

/* Řádek se propisuje, jen když do něj poradce něco zadal. Přednastavená
 * prázdná nabídka z datového modelu do složky klienta nepatří.
 *
 * POZOR na `original_amount`: cizoměnná položka BEZ použitelného kurzu má
 * v `hodnota` nulu (koruny neznáme). Kdyby se sem nedostala, zmizela by ze
 * složky úplně — a přesně o ní poradce potřebuje vědět, že chybí kurz. */
/* Textová pole, kterými poradce položku POPISUJE. Přednastavený řádek
 * z datového modelu je má všechna prázdná, takže se nedá splést s vyplněným.
 *
 * NÁLEZ 7. 8. 2026 (kontrola celé cesty analýza → složka): les zapsaný jako
 * „Pole u Brna" s poznámkou „cenu klient nezná" a bez ceny se do složky
 * NEDOSTAL VŮBEC — evidovanost se poznávala jen podle částky, adresy, SPZ
 * a IČO. Jenže pole a lesy adresu nemají a cenu klient často nezná. Poradce
 * to vyplnil a ono to zmizelo. Proto se počítá i katastr, popis a poznámka. */
const POPISNA_POLE = ["adresa", "spz", "ico", "katastr", "popis", "pozn"];
/* Číselná specifika typu — výměra pozemku, podíl ve firmě, rok výroby auta.
 * Vyplněná výměra je taky důkaz, že řádek není přednastavená prázdnota. */
const CISELNA_POLE = ["vymera", "podil", "rokVyroby"];

/* Řádek se propisuje, jen když do něj poradce něco zadal. Přednastavená
 * prázdná nabídka z datového modelu do složky klienta nepatří.
 *
 * POZOR na `original_amount`: cizoměnná položka BEZ použitelného kurzu má
 * v `hodnota` nulu (koruny neznáme). Kdyby se sem nedostala, zmizela by ze
 * složky úplně — a přesně o ní poradce potřebuje vědět, že chybí kurz. */
const evidovany = (r) =>
  num(r?.hodnota) > 0 || num(r?.original_amount) > 0 || num(r?.prijem) > 0 ||
  POPISNA_POLE.some((k) => !!txt(r?.[k])) ||
  CISELNA_POLE.some((k) => num(r?.[k]) > 0);

/* Dedup klíč — stejný princip jako u smluv (typ + název). Bez něj by opakované
 * uložení analýzy majetek ve složce znásobilo. */
const dedupKlic = (kat, typ, nazev) => `${kat}|${typ}|${nazev.toLowerCase()}`;

/**
 * Jedna položka majetku pro klientskou složku.
 *
 * hodnotaCzk je null vždy, když uplne === false — aby se nedala omylem
 * sečíst ani zobrazit jako ověřené číslo.
 */
function polozka(r, kat, vychozi, lang, dnes) {
  const typ = typNefin(r, vychozi);
  const nazev = txt(r?.oznaceni) || nefinLabel(typ, lang);
  const mena = normMena(r?.currency);
  const cizi = !!mena && mena !== DOMACI_MENA;
  /* U domácí měny je zdrojem pravdy `hodnota`. Nula ale NENÍ ocenění —
   * evidovaná nemovitost bez ceny má do složky jít bez čísla, ne za 0 Kč.
   * Prázdná částka projde přes prepocet jako „chybí částka" i s důvodem. */
  const p = prepocet(
    cizi ? r : { ...r, currency: DOMACI_MENA, original_amount: num(r?.hodnota) > 0 ? num(r.hodnota) : "" },
    dnes,
  );

  return {
    klic: dedupKlic(kat, typ, nazev),
    /* Stabilní klíč řádku analýzy — na něm stojí sync do složky.
     * Schválně JINÝ než `klic` výše: `klic` slučuje omylem zdvojené řádky
     * uvnitř jedné analýzy (dvakrát „Octavia"), `analyzaKlic` drží vazbu
     * na konkrétní řádek. Kdyby sync jel podle názvu, přejmenování bytu
     * z „Byt" na „Byt Praha 6" by ve složce založilo druhý byt. */
    analyzaKlic: `${kat}:${txt(r?.id) || nazev}`,
    kategorie: kat,
    typ,
    typLabel: nefinLabel(typ, lang),
    // Hrubý druh pro filtr v seznamu klientů (nemovitost / vozidlo / …).
    druh: druhMajetku(typ),
    nazev,
    hodnotaCzk: p.uplne ? Math.round(p.czk) : null,
    uplne: p.uplne,
    duvod: p.uplne ? "" : p.duvod,
    duvodText: p.uplne ? null : duvodText(p.duvod, lang),
    mena: mena || DOMACI_MENA,
    puvodniCastka: cizi ? num(r?.original_amount) : null,
    kurz: cizi ? formatKurzu(p.kurzPouzit, lang) : null,
    kurzDatum: cizi ? datumKurzu(r?.fx_updated_at) : null,
    // Měsíční příjem dává smysl jen u výnosových aktiv — u auta ani hodinek
    // ho nedržíme, i kdyby v datech omylem zůstal.
    prijemMesicne: maPrijem(typ) ? num(r?.prijem) : null,
    pojisteno: typeof r?.pojisteno === "boolean" ? r.pojisteno : null,
    adresa: txt(r?.adresa) || null,
    poznamka: txt(r?.pozn) || null,
  };
}

/**
 * Majetek klienta připravený k zápisu do složky.
 *
 * Vrací:
 *   polozky      — všechny evidované položky (i ty bez ceny, s uplne:false)
 *   kOceneni     — podmnožina, u které chybí použitelný kurz
 *   celkemCzk    — součet POUZE oceněných položek
 *   uplne        — true, když se dala ocenit každá položka
 *
 * `dnes` je parametr kvůli testovatelnosti stáří kurzů.
 */
export function majetekProSlozku(d = {}, { lang = "cs", dnes = new Date() } = {}) {
  const m = d?.majetek || {};
  const polozky = [];
  const videne = new Set();

  for (const { kat, vychozi } of ZDROJE) {
    for (const r of arr(m[kat])) {
      if (!evidovany(r)) continue;
      const p = polozka(r, kat, vychozi, lang, dnes);
      // Dva řádky se stejným typem i názvem jsou pro složku jedna věc.
      if (videne.has(p.klic)) continue;
      videne.add(p.klic);
      polozky.push(p);
    }
  }

  const kOceneni = polozky.filter((p) => !p.uplne);
  return {
    polozky,
    kOceneni,
    celkemCzk: polozky.reduce((a, p) => a + (p.hodnotaCzk ?? 0), 0),
    prijemCelkem: polozky.reduce((a, p) => a + (p.prijemMesicne ?? 0), 0),
    uplne: kOceneni.length === 0,
  };
}

/* ══════════════════════════════════════════════════════════════════════
 * MOST DO KLIENTSKÉ SLOŽKY (/api/assets)
 *
 * Zapojeno 7. 8. 2026 — hlavička nahoře ještě mluví o „integration pending",
 * to už neplatí; volá to syncMajetek() v AnalyzaV2Page.js.
 *
 * IDEMPOTENCE stojí na `details.analyza_klic` = `kategorie:id řádku`.
 * Řádek analýzy má ve složce nejvýš jednu položku majetku, ať poradce uloží
 * analýzu jednou nebo padesátkrát.
 *
 * ANALÝZA JE VLASTNÍKEM SVÝCH ŘÁDKŮ:
 * Když poradce v analýze změní cenu bytu z 8 na 9 milionů, sync položku
 * ve složce PŘEPÍŠE (PATCH). Jinak by složka tvrdošíjně ukazovala 8 milionů
 * a poradce by nevěděl, které číslo platí. Položky, které si poradce založil
 * ve složce ručně (source ≠ "analyza"), se nikdy nepřepisují ani nemažou.
 *
 * CO SYNC SCHVÁLNĚ NEDĚLÁ: nemaže. Když poradce řádek z analýzy odstraní,
 * položka ve složce zůstane — automatické maza­ní klientových dat na pozadí
 * je horší než položka navíc, kterou poradce ve složce smaže sám.
 * ══════════════════════════════════════════════════════════════════════ */

/* ── KDO CO VLASTNÍ (oprava 17. 8. 2026) ────────────────────────────────────
 * Do dneška se všech jedenáct polí přepisovalo holým porovnáním, takže `null`
 * z payloadu odešel do PATCHe jako skutečná hodnota. Poradce si ve složce
 * ručně doplnil adresu bytu, poznámku a zaškrtl „pojištěno" — a při nejbližším
 * uložení analýzy (která u toho bytu má jen cenu) mu PATCH poslal
 * `{ address: null, insured: null, notes: null }` a všechno to smazal. Toast
 * u toho hlásil „Majetek ve složce aktualizován (1)".
 *
 * U SMLUV se tatáž chyba opravila 9. 8. 2026 (`analyzaVyplnila` v
 * propsaniSmluv.js), u majetku ji nikdo nedodělal. Backend nepomůže:
 * `AssetUpdateInput` na PATCHi `null` propustí — na rozdíl od `exclude_none`
 * na POSTu.
 *
 * Od 17. 8. 2026 běží propis AUTOMATICKY každých ~12 s, ne jen na stisk
 * Uložit — bez téhle opravy by ruční úpravy ve složce mizely průběžně během
 * schůzky.
 *
 * PRAVIDLO: analýza vlastní jen to, co sama vyplnila. Prázdno v analýze
 * znamená „nevím", ne „smaž, co ví složka".
 * ────────────────────────────────────────────────────────────────────────── */

/* Pole, kterými analýza položku DEFINUJE. Píše je vždy, i prázdné —
 * `value: null` + `value_unknown_reason` je záměrný zápis „cenu neznáme"
 * a musí umět dřív vyplněnou cenu zase shodit na neznámou. */
const POLE_ANALYZY = ["type", "kind", "name", "value", "value_unknown_reason", "currency"];

/* Pole, která ve složce dopisuje PORADCE (adresa, pojištění, poznámka…).
 * Analýza je smí doplnit, když je sama má — nikdy přepsat prázdnem. */
const POLE_PORADCE = ["original_amount", "monthly_income", "address", "insured", "notes"];

/** Pole payloadu, která se porovnávají a případně přepisují při dalším uložení. */
const SYNC_POLE = [...POLE_ANALYZY, ...POLE_PORADCE];

/* Má analýza k tomuhle poli co říct?
 *
 * `false` u zaškrtávátka je PLNOHODNOTNÝ ÚDAJ — poradce v analýze pojištění
 * výslovně odškrtl a to se propsat musí. Model analýzy `pojisteno`
 * nepředvyplňuje (nefinancni.js), takže nedotčený řádek sem přijde jako null.
 *
 * Nula u částky se naopak bere jako nevyplněno — stejné rozhodnutí a stejný
 * důvod jako u smluv: model má u částek výchozí 0 a přepsat poradcem zapsaný
 * příjem z pronájmu nulou je horší než nepropsat skutečnou nulu. */
const analyzaVyplnila = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "boolean") return true;
  if (typeof v === "number") return Number.isFinite(v) && v > 0;
  return txt(v) !== "";
};

/**
 * Položka složky → payload pro POST /api/assets.
 *
 * `value` je `null`, když se položka nedala ocenit. Do POSTu se null pole
 * neposílají (backend by je stejně zahodil), do PATCHe naopak ANO — jen tak
 * se dá dřív vyplněná cena zase vymazat na neznámou.
 */
export function assetPayload(p, contactId) {
  return {
    contact_id: contactId,
    type: p.typ,
    kind: p.druh,
    name: p.nazev,
    // Neoceněná položka nesmí do složky vstoupit s nulou — jde bez čísla
    // a s důvodem, proč cena chybí.
    value: p.uplne ? p.hodnotaCzk : null,
    value_unknown_reason: p.uplne ? null : (p.duvodText || null),
    currency: p.mena || null,
    original_amount: p.puvodniCastka ?? null,
    monthly_income: p.prijemMesicne ?? null,
    address: p.adresa || null,
    insured: typeof p.pojisteno === "boolean" ? p.pojisteno : null,
    notes: p.poznamka || null,
    details: {
      analyza_klic: p.analyzaKlic,
      analyza_kategorie: p.kategorie,
      ...(p.kurz ? { kurz: p.kurz } : {}),
      ...(p.kurzDatum ? { kurz_datum: p.kurzDatum } : {}),
    },
    source: "analyza",
  };
}

/** Payload bez null polí — tvar, který se posílá do POSTu. */
export const bezPrazdnych = (payload) =>
  Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== undefined));

/* Rovnost dvou hodnot téhož pole. undefined z Mongo dokumentu (pole tam
 * prostě není) a null z payloadu znamenají totéž: nevyplněno. Bez tohohle
 * by se PATCH posílal při každém uložení donekonečna. */
const stejne = (a, b) => (a ?? null) === (b ?? null);

/**
 * Co je potřeba ve složce udělat, aby odpovídala analýze.
 *
 * @param polozky     výstup majetekProSlozku(d).polozky
 * @param existujici  co vrátí GET /api/assets?contact_id=…
 * @returns { nove: [payload], zmenene: [{ id, patch }] }
 *
 * Opakované uložení nezměněné analýzy vrátí { nove: [], zmenene: [] }.
 */
export function zmenyMajetku(polozky = [], existujici = [], contactId = "") {
  /* Index podle klíče řádku analýzy. Kdyby ve složce omylem seděly dvě
   * položky s týmž klíčem (např. po ručním kopírování), bereme první —
   * druhou raději necháme být, než abychom ji tiše přepsali. */
  const podleKlice = new Map();
  for (const a of arr(existujici)) {
    const k = txt(a?.details?.analyza_klic);
    if (k && !podleKlice.has(k)) podleKlice.set(k, a);
  }

  const nove = [];
  const zmenene = [];
  const zpracovane = new Set();

  for (const p of arr(polozky)) {
    const payload = assetPayload(p, contactId);
    const klic = payload.details.analyza_klic;
    // Pojistka proti duplicitě uvnitř jedné dávky.
    if (zpracovane.has(klic)) continue;
    zpracovane.add(klic);

    const uz = podleKlice.get(klic);
    if (!uz) { nove.push(bezPrazdnych(payload)); continue; }
    // Ruční položku poradce nepřepisujeme, i kdyby klíč náhodou seděl.
    if (txt(uz.source) && txt(uz.source) !== "analyza") continue;

    const patch = {};
    for (const pole of SYNC_POLE) {
      const nova = payload[pole];
      // Poradcovo pole se přepíše jen tím, co analýza opravdu vyplnila.
      if (POLE_PORADCE.includes(pole) && !analyzaVyplnila(nova)) continue;
      if (!stejne(uz[pole], nova)) patch[pole] = nova;
    }
    if (Object.keys(patch).length) zmenene.push({ id: uz.id, patch });
  }

  return { nove, zmenene };
}
